import StatusBadgeM from "@common/components/badge/StatusBadgeM"
import AvatarM from "@common/components/avatar/AvatarM"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import SatisfactionComponent from "@common/liveMode/SatisfactionComponent"
import LiveReportCardComponent from "@common/liveMode/live-report/LiveReportCardComponent"
import { EngagementScoreBar } from "@common/liveMode/live-report/LiveReportComponent"
import { PaginatedFlatList } from "@common/screens/PaginatedFlatList"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { setOngoingActivity } from "@data/redux/actions/activity"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { setIsLiveModeEnabled } from "@data/redux/actions/liveMode"
import { backOfficeService } from "@data/services/backOfficeService"
import { liveReportService } from "@data/services/liveReportService"
import { AbilityContext } from "@data/utility/Can"
import { capitalize } from "@data/utility/Utils"
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import React, { useContext, useState } from "react"
import { TouchableOpacity, View } from "react-native"
import { IconButton } from "react-native-paper"
import { connect } from "react-redux"
import { PrioritizedSchoolSubjects } from "../../../tutor/students/ProfilDetails"
import { PaginatedTableM } from "../dataTable/PaginatedTableM"

export const EngagementScoreComponent = (props) => {
    const {
        scores,
        totalScore,
        width,
        autoWidth = false,
        disableScoreButton = false,
        forceVerticalDetails = false,
        showTotalScore = true,
    } = props
    return (
        <View
            style={{
                marginLeft: 0,
                maxWidth: 700,
                flexDirection: forceVerticalDetails ? "column" : "row",
                alignItems: "center",
                ...props.style,
            }}
        >
            {!forceVerticalDetails && showTotalScore && (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <TextM
                        fontSize={20}
                        color={
                            totalScore === undefined
                                ? null
                                : totalScore < 50
                                  ? theme.colors.error
                                  : totalScore < 70
                                    ? theme.colors.warning
                                    : theme.colors.success
                        }
                        fontWeight={"ExtraBold"}
                    >
                        {totalScore ?? "-"}{" "}
                        <TextM fontSize={16} fontWeight={"Bold"}>
                            /100
                        </TextM>
                    </TextM>
                </View>
            )}
            <EngagementScoreBar
                scores={scores}
                width={autoWidth ? undefined : (width ?? 400)}
                noProgressBar
                forceVerticalDetails={forceVerticalDetails}
                disableScoreButton={disableScoreButton}
            />
        </View>
    )
}

const LiveReportScreen = (props) => {
    const { view } = props
    const { studentId } = props.route?.params ?? {}

    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)

    // When width < 800, use card view
    const isCardView = view === "cards" || dimensions?.width < 800

    const requestApiService = studentId
        ? liveReportService.getStudentLiveReports
        : backOfficeService.getLiveReports
    const requestRouteParams = studentId ? { id: studentId } : {}

    const [student, setStudent] = useState(null)

    useApiStatus(
        liveReportService.getStudentLiveReports, // api service
        false, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        (successData) => {
            setStudent(successData?.student)
        } // success callback,
    )

    const ability = useContext(AbilityContext)

    const columns = [
        {
            name: "Date du Live",
            style: {
                width: 140,
                marginRight: 20,
            },
            selector: "updated_at",
            sortable: true,
            cell: (row) => {
                const sessionDate = capitalize(
                    moment(
                        row?.statistics?.live_date_time ??
                            row?.live_occurrence?.date
                    ).format("dddd DD MMMM HH:mm")
                )

                return (
                    <TouchableOpacity
                        disabled={!row?.statistics}
                        onPress={() => {
                            navigation?.push("r", {
                                h: row?.hash,
                            })
                        }}
                    >
                        <TextM
                            wrap
                            color={
                                !row?.statistics
                                    ? theme.colors.grey
                                    : theme.colors.primary
                            }
                            fontWeight={!row?.statistics ? "Medium" : "Bold"}
                        >
                            {sessionDate}
                        </TextM>
                        {(row?.live_occurrence?.is_replacement ||
                            row?.is_rattrapage) && (
                            <View
                                style={{
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: 4,
                                    marginTop: 4,
                                    alignSelf: "flex-start",
                                }}
                            >
                                {row?.live_occurrence?.is_replacement && (
                                    <StatusBadgeM
                                        icon="user-friends"
                                        label="Remplacement"
                                        backgroundColor={
                                            theme.colors.warningLight
                                        }
                                        color={theme.colors.warning}
                                    />
                                )}
                                {row?.is_rattrapage && (
                                    <StatusBadgeM
                                        icon="redo"
                                        label="Rattrapage"
                                        backgroundColor={
                                            theme.colors.primaryLighter
                                        }
                                        color={theme.colors.primary}
                                    />
                                )}
                            </View>
                        )}
                    </TouchableOpacity>
                )
            },
        },
        {
            name: "Score d'engagement",
            style: {
                width: 700,
                marginRight: 40,
            },
            selector: "statistics.scores.total_score",
            sortable: true,
            cell: (row) => {
                return !row?.statistics ? (
                    "-"
                ) : (
                    <EngagementScoreComponent
                        totalScore={row?.statistics?.scores?.total_score}
                        width={600}
                        scores={row?.statistics?.scores}
                    />
                )
            },
        },
        {
            name: "Activités validées / envoyées",
            style: {
                width: 120,
                marginRight: 40,
            },
            selector: "statistics.activities_count",
            sortable: true,
            cell: (row) => (
                <>
                    <TextM fontWeight={"SemiBold"} style={{ marginLeft: 5 }}>
                        {row?.statistics?.validated_activities_count ?? 0}/
                        {row?.statistics?.done_activities_count ?? 0}
                    </TextM>
                </>
            ),
        },
        {
            name: !ability.can("read", "backOffice")
                ? ""
                : "Satisfaction élève",
            style: {
                width: 140,
                marginRight: 40,
            },
            selector: "satisfaction",
            sortable: true,
            cell: (row) =>
                !row?.satisfaction || !ability.can("read", "backOffice") ? (
                    "-"
                ) : (
                    <View
                        style={{
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                        }}
                    >
                        <SatisfactionComponent
                            rating={row?.satisfaction}
                            imageSize={20}
                            readonly
                        />
                        <TextM wrap fontSize={10}>
                            {row?.student_comment?.join(", ") ?? "-"}
                        </TextM>
                    </View>
                ),
        },
        {
            name: "Commentaire tuteur",
            style: {
                width: 300,
                marginRight: 40,
            },
            selector: "statistics.activities_count",
            sortable: true,
            cell: (row) => (
                <View
                    style={{
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                    }}
                >
                    <TextM wrap fontSize={10} style={{}}>{`${
                        row?.tutor_display_name ?? "-"
                    }`}</TextM>
                    <TextM wrap fontSize={10} style={{}}>{`${
                        row?.tutor_comment ?? "-"
                    }`}</TextM>
                </View>
            ),
        },
    ]

    if (!studentId) {
        columns.splice(1, 0, {
            name: "Élève",
            style: {
                width: 140,
                marginRight: 40,
            },
            selector: "student",
            sortable: true,
            cell: (row) =>
                !row?.student ? (
                    "-"
                ) : (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            marginRight: 23,
                        }}
                    >
                        <AvatarM user={row?.student} size={45} />
                        <View>
                            <TextM
                                color={theme.colors.primary}
                                fontWeight={"Bold"}
                                fontSize={16}
                                style={{ marginLeft: 10 }}
                            >
                                {row?.student?.display_name}
                            </TextM>
                            <TextM style={{ marginLeft: 10 }}>
                                {row?.student?.profile?.class}
                            </TextM>
                        </View>
                    </View>
                ),
        })
    }

    // Card view rendering for parent mode
    const renderCardView = () => {
        const liveReportsData = studentId
            ? props.liveReports
            : props.backOfficeLiveReports

        return (
            <View
                style={{
                    flex: 1,
                    backgroundColor: theme.colors.whiteAlmost,
                }}
            >
                {!props.isParentMode && (
                    <HeaderContainerM
                        style={{
                            justifyContent: "flex-start",
                            width: "100%",
                            backgroundColor: theme.colors.white,
                            flexDirection: "column",
                            alignItems: "flex-start",
                        }}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <IconButton
                                icon={"times"}
                                iconColor={theme.colors.primary}
                                containerColor={theme.colors.transparent}
                                size={32}
                                style={{
                                    marginRight: 10,
                                }}
                                onPress={() => {
                                    if (navigation.canGoBack())
                                        navigation.goBack()
                                    else navigation.replace("menu")
                                }}
                            />
                            <TitleM>Rapports de sessions</TitleM>
                        </View>
                        <TextM
                            fontSize={14}
                            color={theme.colors.grey}
                            style={{ marginLeft: 56, marginTop: 5 }}
                        >
                            Consultez l&apos;historique de vos sessions et leurs
                            rapports détaillés
                        </TextM>
                    </HeaderContainerM>
                )}

                <PaginatedFlatList
                    key={studentId}
                    data={liveReportsData}
                    requestApiService={requestApiService}
                    requestRouteParams={requestRouteParams}
                    requestUrlParams={{}}
                    style={{
                        width: "100%",
                        backgroundColor: theme.colors.whiteAlmost,
                    }}
                    contentContainerStyle={{
                        paddingHorizontal: 20,
                        paddingTop: 20,
                        paddingBottom: 40,
                        alignItems: "center",
                        width: "100%",
                    }}
                    renderItem={({ item: report, index }) => {
                        const cardWidth =
                            dimensions?.width < 800
                                ? dimensions?.width - 40
                                : 600
                        return (
                            <LiveReportCardComponent
                                key={report?.id || index}
                                report={report}
                                style={{
                                    width: cardWidth,
                                }}
                            />
                        )
                    }}
                />
            </View>
        )
    }

    // Table view rendering (default for admin)
    const renderTableView = () => (
        <View
            style={{
                flex: 1,
            }}
        >
            <HeaderContainerM
                style={{ justifyContent: "space-between", width: "100%" }}
            >
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <IconButton
                        icon={"times"}
                        iconColor={theme.colors.primary}
                        containerColor={theme.colors.transparent}
                        size={32}
                        style={{
                            marginRight: 10,
                        }}
                        onPress={() => {
                            if (navigation.canGoBack()) navigation.goBack()
                            else navigation.replace("menu")
                        }}
                    />
                    <TitleM>Rapports des Lives</TitleM>
                </View>

                {studentId && (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            marginRight: 23,
                        }}
                    >
                        <AvatarM user={student} size={45} />
                        <View>
                            <TextM
                                color={theme.colors.primary}
                                fontWeight={"Bold"}
                                fontSize={16}
                                style={{ marginLeft: 10 }}
                            >
                                {student?.display_name}
                            </TextM>
                            <TextM style={{ marginLeft: 10 }}>
                                {student?.profile?.class}
                            </TextM>
                        </View>
                        {student?.prioritized_school_subjects?.length > 0 ? (
                            <PrioritizedSchoolSubjects
                                student={student}
                                style={{
                                    marginLeft: 12,
                                    flexDirection: "column",
                                }}
                            />
                        ) : (
                            <TextM style={{ marginLeft: 12 }}>-</TextM>
                        )}
                    </View>
                )}
            </HeaderContainerM>
            <PaginatedTableM
                data={
                    studentId ? props.liveReports : props.backOfficeLiveReports
                }
                flatListRef={null}
                columns={columns}
                headerHeight={60}
                requestService={requestApiService}
                routeParams={requestRouteParams}
                urlParams={{}}
            />
        </View>
    )

    // Render appropriate view based on isCardView flag
    return isCardView ? renderCardView() : renderTableView()
}
const mapStateToProps = (state) => {
    return {
        liveReports: state.data?.liveReports?.liveReports,
        backOfficeLiveReports: state.data?.backOffice?.liveReports,
        users: state.data?.users?.usersList,
        getStudentLiveReportsRequest: state.api.getStudentLiveReports?.request,
        getStudentLiveReportsError: state.api.getStudentLiveReports?.error,
        isParentMode: state.data?.family?.isParentMode,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setOngoingActivity,
    setIsLiveModeEnabled,
}

export default connect(mapStateToProps, mapDispatchToProps)(LiveReportScreen)
